/**
 * Hero Slider Section
 * Carousel of hero images with autoplay
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import useStorefrontTheme from '../../hooks/useStorefrontTheme';
import './sections.css';

function HeroSlider({ settings = {}, content = {}, globalStyles = {}, store }) {
  const {
    // Slider slides are absolutely positioned within the track,
    // so the section needs a defined height (not min-height) for
    // them to fill. Hard-coding 600px was fine on desktop but ate
    // ~70% of an iPhone's viewport on mobile. The clamp lets the
    // slider scale down to 420px on phones and up to 600px on
    // desktop without ever pinning a value that's wrong everywhere.
    height = 'clamp(420px, 60vw, 600px)',
    autoplay = true,
    interval = 5000,
    showDots = true,
    showArrows = true,
    overlayOpacity = 0.2,
    overlayColor = '#000000',
    darkOverlayOpacity,
    darkOverlayColor,
    // Style settings
    titleColor = '#ffffff',
    subtitleColor = '#e5e5e5',
    buttonBgColor,
    buttonTextColor = '#ffffff',
    darkTitleColor,
    darkSubtitleColor,
    darkButtonBgColor,
    darkButtonTextColor,
    buttonStyle = 'filled',
    // Pill CTA default — matches HeroBanner + StorefrontHero.
    buttonRadius = '999px',
  } = settings;

  const { slides = [] } = content;
  const [currentIndex, setCurrentIndex] = useState(0);

  const { resolvedTheme } = useStorefrontTheme();
  const isDark = resolvedTheme === 'dark';
  const resolvedOverlayOpacity = isDark && darkOverlayOpacity !== undefined ? darkOverlayOpacity : overlayOpacity;
  const resolvedOverlayColor = isDark ? (darkOverlayColor || overlayColor) : overlayColor;
  const baseColors = globalStyles.colors || {};
  const colors = isDark ? { ...baseColors, ...(globalStyles.darkColors || {}) } : baseColors;
  const fonts = globalStyles.fonts || {};
  const resolvedTitleColor = (isDark ? darkTitleColor : titleColor) || (isDark ? colors.text || 'var(--theme-dark-text, #f3f4f6)' : '#ffffff');
  const resolvedSubtitleColor = (isDark ? darkSubtitleColor : subtitleColor) || (isDark ? colors.muted || 'var(--theme-dark-muted, #9ca3af)' : '#e5e5e5');

  // Default slides if none provided
  const displaySlides = slides.length > 0 ? slides : [
    {
      title: 'Welcome to Our Store',
      subtitle: 'Discover our latest collection',
      buttonText: 'Shop Now',
      buttonLink: '/shop',
      backgroundImage: '',
    },
  ];

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % displaySlides.length);
  }, [displaySlides.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + displaySlides.length) % displaySlides.length);
  }, [displaySlides.length]);

  useEffect(() => {
    if (!autoplay || displaySlides.length <= 1) return;

    const timer = setInterval(nextSlide, interval);
    return () => clearInterval(timer);
  }, [autoplay, interval, nextSlide, displaySlides.length]);

  const containerStyle = {
    height,
  };

  // Build button styles based on buttonStyle setting
  const primaryColor = (isDark ? darkButtonBgColor : buttonBgColor) || colors.primary || '#2563eb';
  const btnStyleObj = {
    borderRadius: buttonRadius,
    padding: globalStyles.buttons?.padding || '14px 32px',
  };

  if (buttonStyle === 'outline') {
    btnStyleObj.backgroundColor = 'transparent';
    btnStyleObj.border = `2px solid ${primaryColor}`;
    btnStyleObj.color = primaryColor;
  } else if (buttonStyle === 'ghost') {
    btnStyleObj.backgroundColor = 'transparent';
    btnStyleObj.color = primaryColor;
  } else {
    btnStyleObj.backgroundColor = primaryColor;
    btnStyleObj.color = (isDark ? darkButtonTextColor : buttonTextColor) || (isDark ? colors.background || 'var(--theme-dark-background, #0b0f17)' : '#ffffff');
  }

  // Parse overlay color to RGB for opacity
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '0, 0, 0';
  };

  return (
    <section className="ts-hero-slider" style={containerStyle}>
      <div className="ts-hero-slider__track">
        {displaySlides.map((slide, index) => (
          <div
            key={index}
            className={`ts-hero-slider__slide ${index === currentIndex ? 'ts-hero-slider__slide--active' : ''}`}
            style={{
              backgroundImage: slide.backgroundImage ? `url(${slide.backgroundImage})` : undefined,
              backgroundColor: slide.backgroundImage ? undefined : (colors.primary || '#1a1a2e'),
            }}
          >
            <div
              className="ts-hero-slider__overlay"
              style={{ backgroundColor: `rgba(${hexToRgb(resolvedOverlayColor)}, ${resolvedOverlayOpacity})` }}
            />
            <div className="ts-hero-slider__content">
              <h1 className="ts-hero-slider__title" style={{ fontFamily: fonts.heading, color: resolvedTitleColor }}>
                {slide.title}
              </h1>
              {slide.subtitle && (
                <p className="ts-hero-slider__subtitle" style={{ color: resolvedSubtitleColor }}>{slide.subtitle}</p>
              )}
              {slide.buttonText && (
                <Link
                  to={slide.buttonLink || '/shop'}
                  className="ts-hero-slider__button"
                  style={btnStyleObj}
                >
                  {slide.buttonText}
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>

      {showArrows && displaySlides.length > 1 && (
        <>
          <button
            className="ts-hero-slider__arrow ts-hero-slider__arrow--prev"
            onClick={prevSlide}
            aria-label="Previous slide"
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            className="ts-hero-slider__arrow ts-hero-slider__arrow--next"
            onClick={nextSlide}
            aria-label="Next slide"
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </>
      )}

      {showDots && displaySlides.length > 1 && (
        <div className="ts-hero-slider__dots">
          {displaySlides.map((_, index) => (
            <button
              key={index}
              className={`ts-hero-slider__dot ${index === currentIndex ? 'ts-hero-slider__dot--active' : ''}`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default React.memo(HeroSlider);
